/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {VoidEventHandler} from '@ion/core/src/types/general';
import {BulkOperationResults} from '@ion/core/src/types/services';
import {IonSnackbar} from '../notifications/IonSnackbar';
import {BulkImportResultsNotification} from './BulkImportResultsNotification';

// defines the prop definitions for results prompt ...
export interface BulkUploadResultsPromptType {
  entity: string;
  valid: boolean;
  isImporting: boolean;
  show: boolean;
  isComplete: boolean;
  isError: boolean;
  result: BulkOperationResults;
  entityLabel: string;
  onExport?: (errorsOnly: boolean) => void;
  onConfirm?: VoidEventHandler;
  onRestart?: VoidEventHandler;
  itemsCount?: number;
  suppressResultsNotification?: boolean;
};

/**
 * The BulkUploadResultsPrompt handles the display of toast message and results
 * post bulk import is initiated.
 *
 * ```typescript
 *
 *   import {BulkUploadResultsPrompt} from '@ion/dls/src/imports';
 *
 *   <BulkUploadResultsPrompt entity={entity}
 *     entityLabel={translate('common:entityLabels.assets')}
 *     result={result}
 *     valid={valid}
 *     isImporting={isImporting}
 *     show={show}
 *     isComplete={isComplete}
 *     isError={isError}
 *     onExport={onExport}
 *     onConfirm={onConfirm}
 *     onRestart={onRestart} />
 * ```
 *
 * @param onExport callback to be executed if user begins export
 * @param onConfirm callback to execute when import results are generated and user confirms
 * the same
 * @param onRestart callback to execute when user wants to start a fresh import
 * @param entity The current CTCEntity under bulk import
 * @param entityLabel The current CTCEntity label under bulk import
 * @param result The result object which has results/errors post import
 * completion
 * @param valid checks if results have invalid entities which helps in generating error excel
 * data
 * @param isImporting checks is import is in progress
 * @param isComplete checks if import is sucessfully completed
 * @param isError checks if import failed
 */
export const BulkUploadResultsPrompt = ({
  onExport,
  onConfirm,
  onRestart,
  entity,
  result,
  entityLabel,
  valid,
  isImporting,
  show,
  isComplete,
  isError,
  itemsCount = 0,
  suppressResultsNotification = false
}: BulkUploadResultsPromptType) => {
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (!suppressResultsNotification || isImporting) {
      setShowToast(isImporting);
    }
  }, [isImporting]);

  function onClose() {
    setShowToast(false);
    if (suppressResultsNotification) {
      onConfirm?.();
    }
  }

  return (
    <>
      {showToast && (
        <IonSnackbar show
          message={translate('common:editors.import.results.progress', {
            entityLabel
          })}
          actionLabel={translate('common:ok')}
          onAction={onClose}
          onClose={onClose}
          autoHideDuration={3000} />
      )}
      {(isComplete || isError) && !suppressResultsNotification && (
        <BulkImportResultsNotification show={show}
          capitalize
          title={translate('common:editors.import.results.title', {entityLabel})}
          actionLabel={translate('common:editors.import.results.onConfirm')}
          dismissLabel={translate('common:editors.import.results.onRestart')}
          entityLabel={entityLabel}
          isError={isError}
          result={result}
          onExport={onExport}
          onAction={onConfirm}
          onDismiss={onRestart}
          valid={valid}
          itemsCount={itemsCount} />
      )}
    </>
  );
};

export default BulkUploadResultsPrompt;
